import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, InputAdornment, Slider, TextField, Tooltip, Typography } from '@mui/material';
import { Calculate, Close, DragIndicator, OpenInFull, Refresh } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import type { AnalysisRanges } from './analysisTypes';

const DILATION_MARKS = [{ value: 2 }, { value: 3 }, { value: 4 }, { value: 5 }, { value: 6 }];

// BUG-2026-0211: the dilation window is per stage — say so on the control so
// nobody expects Apply to touch the other stages any more.
const dilationTooltip = (stageNumber?: number) => stageNumber != null
  ? `Dilation averaging window for Stage ${stageNumber} only — Apply recalculates just this stage; every other stage keeps its own value.`
  : 'Dilation averaging window (mm)';

interface PeakResidualInputsProps {
  ranges: AnalysisRanges;
  onChange: (ranges: AnalysisRanges) => void;
  // "Calculate" (renamed from "Highlight", 2026-06-12 review) — computes &
  // persists Peak/Residual across all stages.
  onCalculate: () => void;
  saving: boolean;
  /** Stage this row belongs to — the dilation window below is scoped to it (BUG-2026-0211). */
  stageNumber?: number;
  dilationRange?: string;
  onDilationRangeChange?: (v: string) => void;
  onApplyRecalculate?: () => void;
  recalculating?: boolean;
  recalcProgress?: string;
  readOnly?: boolean;
}

// BUG-2026-0199 #1: the Peak/Residual From/To boxes rejected any value with a
// zero right after the decimal — "6.09" auto-corrected to "69". Root cause: the
// fields were controlled `type=number` inputs whose value was round-tripped
// through parseFloat→number→String() on every keystroke, so "6.0" collapsed to
// "6" mid-typing and the next digit produced "69". RangeNumberField keeps the
// raw typed text in local state (so "6.", "6.0", "0.09" survive) and only
// converts to a number when handing the value up to the parent.
interface RangeNumberFieldProps {
  label: string;
  value: number | null;
  disabled?: boolean;
  onChange: (v: number | null) => void;
}

const RangeNumberField = ({ label, value, disabled, onChange }: RangeNumberFieldProps) => {
  const [text, setText] = useState<string>(value != null ? String(value) : '');

  // Re-sync the buffer only when the stored value changes from OUTSIDE this
  // field (initial load, reset, "Calculate" write-back). For the user's own
  // edits the parsed buffer already equals `value`, so we leave the raw text
  // untouched and keep trailing "." / "0" visible while typing.
  useEffect(() => {
    const trimmed = text.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    const parsedValid = parsed != null && !Number.isNaN(parsed) ? parsed : null;
    if (parsedValid !== value) {
      setText(value != null ? String(value) : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (raw: string) => {
    // Accept only a partial decimal number as it is typed (optional leading
    // minus, digits, at most one dot). Reject anything else so the buffer never
    // holds garbage.
    if (raw !== '' && !/^-?\d*\.?\d*$/.test(raw)) return;
    setText(raw);
    if (raw === '' || raw === '-' || raw === '.' || raw === '-.') {
      onChange(null);
      return;
    }
    const n = Number(raw);
    onChange(Number.isNaN(n) ? null : n);
  };

  return (
    <TextField
      size="small"
      type="text"
      label={label}
      value={text}
      disabled={disabled}
      onChange={(e) => handleChange(e.target.value)}
      inputProps={{ inputMode: 'decimal' }}
      sx={{ width: 88 }}
      InputProps={{ sx: { fontSize: '0.7rem', py: 0 } }}
      InputLabelProps={{ sx: { fontSize: '0.68rem' } }}
    />
  );
};

export const PeakResidualInputs = ({
  ranges, onChange, onCalculate, saving, stageNumber,
  dilationRange, onDilationRangeChange, onApplyRecalculate, recalculating, recalcProgress,
  readOnly,
}: PeakResidualInputsProps) => {
  const theme = useTheme();
  // Docked-by-default: the data-entry row should sit at the top of the page on
  // first load (BUG-2026-0042). Users who explicitly switch to floating get
  // their preference remembered via localStorage.
  const [floating, setFloating] = useState(() => localStorage.getItem('belab-range-bar-docked') === 'false');
  const [minimized, setMinimized] = useState(false);

  // Draggable position
  const [pos, setPos] = useState<{ x: number; y: number }>(() => {
    try { const s = localStorage.getItem('belab-range-bar-pos'); if (s) return JSON.parse(s); } catch {}
    return { x: 300, y: 70 };
  });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const savePos = useCallback((p: { x: number; y: number }) => {
    setPos(p);
    localStorage.setItem('belab-range-bar-pos', JSON.stringify(p));
  }, []);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  }, [pos]);

  useEffect(() => {
    if (!floating) return;
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      savePos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [floating, savePos]);

  const toggleFloat = () => {
    const next = !floating;
    setFloating(next);
    setMinimized(false);
    localStorage.setItem('belab-range-bar-docked', next ? 'false' : 'true');
  };

  // ── Floating minimized: tiny pill ──
  if (floating && minimized) {
    return (
      <Box sx={{
        position: 'fixed', left: pos.x, top: pos.y, zIndex: 1300,
        display: 'flex', alignItems: 'center', gap: 0.5,
        px: 1, py: 0.25, borderRadius: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.9),
        color: '#fff', cursor: 'grab', boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
        userSelect: 'none', fontSize: '0.7rem', fontWeight: 600,
      }}
        onMouseDown={onMouseDown}
      >
        <DragIndicator sx={{ fontSize: 12 }} />
        Ranges
        <IconButton size="small" onClick={() => setMinimized(false)} sx={{ color: '#fff', p: 0.2 }}>
          <OpenInFull sx={{ fontSize: 12 }} />
        </IconButton>
      </Box>
    );
  }

  // ── Floating expanded: compact draggable panel ──
  if (floating) {
    return (
      <Box sx={{
        position: 'fixed', left: pos.x, top: pos.y, zIndex: 1300,
        display: 'flex', flexDirection: 'column', gap: 0.5,
        p: 0.75, borderRadius: 2,
        backgroundColor: alpha(theme.palette.background.paper, 0.97),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        minWidth: 280,
      }}>
        {/* Title bar — draggable */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'grab',
          px: 0.5, py: 0.15, borderRadius: 1,
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          userSelect: 'none',
        }} onMouseDown={onMouseDown}>
          <DragIndicator sx={{ fontSize: 12, color: 'text.secondary' }} />
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, flex: 1, color: 'text.secondary' }}>
            Range Controls
          </Typography>
          <Tooltip title="Minimize"><IconButton size="small" onClick={() => setMinimized(true)} sx={{ p: 0.15 }}>
            <Close sx={{ fontSize: 12 }} />
          </IconButton></Tooltip>
          <Tooltip title="Dock to page"><IconButton size="small" onClick={toggleFloat} sx={{ p: 0.15 }}>
            <OpenInFull sx={{ fontSize: 12 }} />
          </IconButton></Tooltip>
        </Box>

        {/* Row 1: Peak + Residual + Highlight */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, whiteSpace: 'nowrap' }}>Peak</Typography>
          <RangeNumberField label="From" value={ranges.peakRangeFrom} disabled={readOnly}
            onChange={(v) => onChange({ ...ranges, peakRangeFrom: v })} />
          <RangeNumberField label="To" value={ranges.peakRangeTo} disabled={readOnly}
            onChange={(v) => onChange({ ...ranges, peakRangeTo: v })} />
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, whiteSpace: 'nowrap', ml: 0.5 }}>Res</Typography>
          <RangeNumberField label="From" value={ranges.residualRangeFrom} disabled={readOnly}
            onChange={(v) => onChange({ ...ranges, residualRangeFrom: v })} />
          <RangeNumberField label="To" value={ranges.residualRangeTo} disabled={readOnly}
            onChange={(v) => onChange({ ...ranges, residualRangeTo: v })} />
          <Button variant="contained" size="small" onClick={onCalculate} disabled={readOnly || saving}
            sx={{ textTransform: 'none', fontSize: '0.66rem', py: 0.2, px: 0.75, minWidth: 0 }}>
            {saving ? '...' : 'Calculate'}
          </Button>
        </Box>

        {/* Row 2: Dilation slider + input + Apply */}
        {onApplyRecalculate && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={dilationTooltip(stageNumber)}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'help' }}>
                {stageNumber != null ? `Dil S${stageNumber}` : 'Dil'}
              </Typography>
            </Tooltip>
            <Slider size="small"
              value={parseFloat(dilationRange ?? '0.375') || 4}
              onChange={(_e, v) => onDilationRangeChange?.(String(v as number))}
              min={2} max={6} step={0.1} marks={DILATION_MARKS}
              disabled={readOnly}
              valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}mm`}
              sx={{
                flex: 1, mx: 0.5,
                '& .MuiSlider-markLabel': { display: 'none' },
                '& .MuiSlider-mark': { height: 4, backgroundColor: alpha(theme.palette.primary.main, 0.2) },
                '& .MuiSlider-thumb': { width: 12, height: 12 },
                '& .MuiSlider-valueLabel': { fontSize: '0.6rem' },
                py: 0,
              }} />
            <TextField size="small"
              value={dilationRange ?? '0.375'}
              onChange={(e) => onDilationRangeChange?.(e.target.value.replace(/[^0-9.]/g, ''))}
              disabled={readOnly}
              inputProps={{ style: { textAlign: 'center', width: 52, fontSize: '0.68rem' } }}
              InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& p': { fontSize: '0.58rem' } }}>mm</InputAdornment> }}
              sx={{ width: 100, '& .MuiOutlinedInput-root': { py: 0 } }} />
            <Button size="small" variant="outlined" disabled={readOnly || recalculating} onClick={onApplyRecalculate}
              startIcon={recalculating ? <CircularProgress size={10} color="inherit" /> : <Refresh sx={{ fontSize: 12 }} />}
              sx={{ textTransform: 'none', fontSize: '0.63rem', py: 0.15, px: 0.5, minWidth: 0 }}>
              {recalculating ? '...' : 'Apply'}
            </Button>
          </Box>
        )}
        {recalculating && recalcProgress && (
          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.6rem' }}>{recalcProgress}</Typography>
        )}
      </Box>
    );
  }

  // ── Docked mode — inline ──
  return (
    <Box sx={{
      display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center',
      px: 1.5, py: 0.5, mb: 1.5,
      border: '1px solid', borderColor: 'divider', borderRadius: 1.5, backgroundColor: 'background.paper',
    }}>
      <Tooltip title="Float (drag anywhere)">
        <IconButton size="small" onClick={toggleFloat} sx={{ p: 0.3 }}>
          <OpenInFull sx={{ fontSize: 14, color: 'text.secondary' }} />
        </IconButton>
      </Tooltip>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>Peak</Typography>
      <RangeNumberField label="From" value={ranges.peakRangeFrom} disabled={readOnly}
        onChange={(v) => onChange({ ...ranges, peakRangeFrom: v })} />
      <RangeNumberField label="To" value={ranges.peakRangeTo} disabled={readOnly}
        onChange={(v) => onChange({ ...ranges, peakRangeTo: v })} />
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, ml: 0.5 }}>Residual</Typography>
      <RangeNumberField label="From" value={ranges.residualRangeFrom} disabled={readOnly}
        onChange={(v) => onChange({ ...ranges, residualRangeFrom: v })} />
      <RangeNumberField label="To" value={ranges.residualRangeTo} disabled={readOnly}
        onChange={(v) => onChange({ ...ranges, residualRangeTo: v })} />
      <Button variant="contained" size="small" startIcon={<Calculate />} onClick={onCalculate} disabled={readOnly || saving}
        sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.3 }}>
        {saving ? '...' : 'Calculate'}
      </Button>
      {onApplyRecalculate && (
        <>
          <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', height: 20, mx: 0.25 }} />
          <Tooltip title={dilationTooltip(stageNumber)}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'help' }}>
              {stageNumber != null ? `Dilation (Stage ${stageNumber}):` : 'Dilation:'}
            </Typography>
          </Tooltip>
          <Slider size="small" value={parseFloat(dilationRange ?? '4') || 4}
            onChange={(_e, v) => onDilationRangeChange?.(String(v as number))}
            min={2} max={6} step={0.1} marks={DILATION_MARKS}
            disabled={readOnly}
            valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}mm`}
            sx={{ width: 100, '& .MuiSlider-markLabel': { display: 'none' }, '& .MuiSlider-mark': { height: 3 }, '& .MuiSlider-thumb': { width: 12, height: 12 }, '& .MuiSlider-valueLabel': { fontSize: '0.6rem' }, py: 0 }} />
          <TextField size="small" value={dilationRange ?? '4'} disabled={readOnly}
            onChange={(e) => onDilationRangeChange?.(e.target.value.replace(/[^0-9.]/g, ''))}
            inputProps={{ style: { textAlign: 'center', width: 52, fontSize: '0.7rem' } }}
            InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& p': { fontSize: '0.6rem' } }}>mm</InputAdornment> }}
            sx={{ width: 100, '& .MuiOutlinedInput-root': { py: 0 } }} />
          <Button size="small" variant="outlined" disabled={readOnly || recalculating} onClick={onApplyRecalculate}
            startIcon={recalculating ? <CircularProgress size={10} /> : <Refresh sx={{ fontSize: 12 }} />}
            sx={{ textTransform: 'none', fontSize: '0.68rem', py: 0.2, px: 0.5 }}>
            {recalculating ? '...' : 'Apply'}
          </Button>
        </>
      )}
    </Box>
  );
};
